import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { 
  Crown, Calendar, CreditCard, Settings, ArrowLeft, 
  CheckCircle, AlertTriangle, Sparkles, TrendingUp,
  BarChart3, Users, Heart
} from 'lucide-react';
import axios from 'axios';

const SubscriptionStatus = () => {
  const navigate = useNavigate();
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(false);

  useEffect(() => {
    fetchSubscriptionStatus();
  }, []);

  const fetchSubscriptionStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/subscription/status`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      setSubscriptionData(response.data);
    } catch (error) {
      console.error('Error fetching subscription status:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!window.confirm('Are you sure you want to cancel your subscription? You will lose access to premium features at the end of your billing period.')) {
      return;
    }

    setCancelling(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/subscription/cancel`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      // Refresh subscription status
      await fetchSubscriptionStatus();
      alert('Subscription cancelled successfully. You will retain access until the end of your billing period.');
    } catch (error) {
      console.error('Error cancelling subscription:', error);
      alert('Failed to cancel subscription. Please try again or contact support.');
    } finally {
      setCancelling(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'bg-green-500/20 text-green-400 border-green-500/30';
      case 'cancelled':
        return 'bg-amber-500/20 text-amber-400 border-amber-500/30';
      case 'expired':
        return 'bg-red-500/20 text-red-400 border-red-500/30';
      default:
        return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
    }
  };

  const getTierIcon = (tier) => {
    switch (tier) {
      case 'premium':
        return <Sparkles className="w-5 h-5" />;
      case 'vip':
        return <Crown className="w-5 h-5" />;
      default:
        return <Heart className="w-5 h-5" />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-2 border-rose-500 border-t-transparent mx-auto mb-4"></div>
          <p className="text-white">Loading subscription details...</p>
        </div>
      </div>
    );
  }

  const { subscription, usage_stats, is_premium, is_vip } = subscriptionData || {};

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(236,72,153,0.1),transparent_70%)]"></div>
      
      <div className="relative z-10 max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <Button
            onClick={() => navigate(-1)}
            variant="outline"
            className="bg-white/10 border-white/20 text-white hover:bg-white/20"
          >
            <ArrowLeft className="mr-2" size={16} />
            Back
          </Button>
          
          <div className="text-center">
            <h1 className="text-3xl font-bold text-white flex items-center">
              <Settings className="mr-3 text-rose-400" size={32} />
              Subscription Management
            </h1>
            <p className="text-gray-300 mt-1">Manage your Shot Clock Dating subscription</p>
          </div>
          
          <div className="w-20"></div> {/* Spacer for centering */}
        </div>

        <div className="grid gap-6">
          {/* Current Subscription Status */}
          <Card className="bg-white/5 backdrop-blur-md border-white/10">
            <CardHeader>
              <CardTitle className="text-white flex items-center justify-between">
                <div className="flex items-center">
                  <CreditCard className="mr-2 text-rose-400" size={24} />
                  Current Subscription
                </div>
                {subscription && (
                  <Badge className={`${getStatusColor(subscription.status)} border`}>
                    {subscription.status.toUpperCase()}
                  </Badge>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {subscription ? (
                <div className="space-y-6">
                  <div className="flex items-center justify-between p-6 bg-gradient-to-r from-rose-500/10 to-pink-500/10 border border-rose-500/20 rounded-xl">
                    <div className="flex items-center">
                      <div className="bg-gradient-to-r from-rose-500 to-pink-500 rounded-full p-3 mr-4">
                        {getTierIcon(subscription.tier)}
                      </div>
                      <div>
                        <h3 className="text-xl font-semibold text-white capitalize">
                          {subscription.tier} Plan
                        </h3>
                        <p className="text-gray-300">
                          {subscription.status === 'active' ? 'Active subscription' : 'Cancelled - access until end date'}
                        </p>
                      </div>
                    </div>
                    
                    <div className="text-right">
                      <div className="text-green-400 font-semibold flex items-center">
                        <CheckCircle className="w-4 h-4 mr-1" />
                        {is_premium ? 'Premium Active' : is_vip ? 'VIP Active' : 'Basic'}
                      </div>
                    </div>
                  </div>

                  <div className="grid md:grid-cols-3 gap-4">
                    <div className="bg-white/5 rounded-lg p-4">
                      <div className="text-gray-400 text-sm mb-1">Start Date</div>
                      <div className="text-white font-semibold">{formatDate(subscription.start_date)}</div>
                    </div>
                    
                    <div className="bg-white/5 rounded-lg p-4">
                      <div className="text-gray-400 text-sm mb-1">End Date</div>
                      <div className="text-white font-semibold">{formatDate(subscription.end_date)}</div>
                    </div>
                    
                    <div className="bg-white/5 rounded-lg p-4">
                      <div className="text-gray-400 text-sm mb-1">Auto Renew</div>
                      <div className="text-white font-semibold">
                        {subscription.auto_renew ? (
                          <span className="text-green-400">Enabled</span>
                        ) : (
                          <span className="text-amber-400">Disabled</span>
                        )}
                      </div>
                    </div>
                  </div>

                  {subscription.status === 'active' && (
                    <div className="flex justify-end">
                      <Button
                        onClick={handleCancelSubscription}
                        disabled={cancelling}
                        variant="outline"
                        className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                      >
                        {cancelling ? 'Cancelling...' : 'Cancel Subscription'}
                      </Button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Heart className="w-16 h-16 text-gray-500 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-white mb-2">No Active Subscription</h3>
                  <p className="text-gray-300 mb-6">
                    You're currently on the free plan with limited features.
                  </p>
                  <Button
                    onClick={() => navigate('/subscription/plans')}
                    className="bg-gradient-to-r from-rose-500 to-pink-500 hover:from-rose-600 hover:to-pink-600 text-white"
                  >
                    Upgrade to Premium
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Usage Statistics */}
          <Card className="bg-white/5 backdrop-blur-md border-white/10">
            <CardHeader>
              <CardTitle className="text-white flex items-center">
                <BarChart3 className="mr-2 text-rose-400" size={24} />
                Usage Statistics
              </CardTitle>
            </CardHeader>
            <CardContent>
              {usage_stats && Object.keys(usage_stats).length > 0 ? (
                <div className="grid md:grid-cols-2 gap-4">
                  {Object.entries(usage_stats).map(([feature, stats]) => (
                    <div key={feature} className="bg-white/5 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="text-white font-medium capitalize">
                          {feature.replace('_', ' ')}
                        </h4>
                        <Badge variant="outline" className="text-xs">
                          {stats.used}/{stats.limit === null ? '∞' : stats.limit}
                        </Badge>
                      </div>
                      
                      <div className="w-full bg-gray-700 rounded-full h-2 mb-2">
                        <div
                          className="bg-gradient-to-r from-rose-500 to-pink-500 h-2 rounded-full transition-all duration-300"
                          style={{
                            width: stats.limit === null ? '100%' : `${Math.min((stats.used / stats.limit) * 100, 100)}%`
                          }}
                        ></div>
                      </div>
                      
                      <div className="text-xs text-gray-400">
                        {stats.limit === null ? 'Unlimited' : `Resets ${formatDate(stats.reset_date)}`}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <TrendingUp className="w-12 h-12 text-gray-500 mx-auto mb-4" />
                  <p className="text-gray-300">No usage data available</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Quick Actions */}
          <Card className="bg-white/5 backdrop-blur-md border-white/10">
            <CardHeader>
              <CardTitle className="text-white flex items-center">
                <Settings className="mr-2 text-rose-400" size={24} />
                Quick Actions
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-3 gap-4">
                <Button
                  onClick={() => navigate('/subscription/plans')}
                  className="bg-gradient-to-r from-rose-500 to-pink-500 hover:from-rose-600 hover:to-pink-600 text-white p-6 h-auto flex-col"
                >
                  <Crown className="w-8 h-8 mb-2" />
                  <span className="font-semibold">Upgrade Plan</span>
                  <span className="text-xs opacity-80">Get more features</span>
                </Button>
                
                <Button
                  onClick={() => navigate('/speed-dating')}
                  variant="outline"
                  className="border-white/20 text-white hover:bg-white/10 p-6 h-auto flex-col"
                >
                  <Users className="w-8 h-8 mb-2" />
                  <span className="font-semibold">Start Dating</span>
                  <span className="text-xs opacity-80">Join a session</span>
                </Button>
                
                <Button
                  onClick={() => navigate('/profile')}
                  variant="outline"
                  className="border-white/20 text-white hover:bg-white/10 p-6 h-auto flex-col"
                >
                  <Heart className="w-8 h-8 mb-2" />
                  <span className="font-semibold">Edit Profile</span>
                  <span className="text-xs opacity-80">Update your info</span>
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Billing Info Notice */}
          <Card className="bg-amber-500/10 border-amber-500/20">
            <CardContent className="p-4">
              <div className="flex items-center">
                <AlertTriangle className="w-5 h-5 text-amber-400 mr-3 flex-shrink-0" />
                <div className="text-amber-200 text-sm">
                  <strong>Note:</strong> Billing is managed securely through Stripe. 
                  For billing inquiries or to update payment methods, please contact support at support@shotclockdating.com
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionStatus;